import { useState, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { Phone, Mail, Check } from 'lucide-react'
import { contactCTAs } from './config'
import type { ChatMessageProps, ContactCTACopy } from '@brochure/shared'

/**
 * Parse `**bold**` markdown into React nodes. Keeps the rest of the text as-is.
 */
function parseMarkdownBold(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      )
    }
    return part
  })
}

const ctaIcons: Record<string, React.ReactNode> = {
  phone: <Phone className="w-3 h-3" />,
  email: <Mail className="w-3 h-3" />,
}

function BotAvatar() {
  return (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-maia text-white shadow-sm">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        className="w-4 h-4"
        aria-hidden="true"
      >
        <path
          d="M7.5 4.5C7.5 4.5 7 6 7.5 7"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
        />
        <path
          d="M6.5 3.5C6.5 3.5 8 2.5 8.5 3.5C9 4.5 7.5 5.5 6.5 5C5.5 4.5 6.5 3.5 6.5 3.5Z"
          fill="currentColor"
        />
        <path
          d="M16.5 4.5C16.5 4.5 17 6 16.5 7"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
        />
        <path
          d="M17.5 3.5C17.5 3.5 16 2.5 15.5 3.5C15 4.5 16.5 5.5 17.5 5C18.5 4.5 17.5 3.5 17.5 3.5Z"
          fill="currentColor"
        />
        <rect
          x="4"
          y="6.5"
          width="16"
          height="13"
          rx="5"
          stroke="currentColor"
          strokeWidth="1.75"
        />
        <circle cx="9.5" cy="11.5" r="1.25" fill="currentColor" />
        <circle cx="14.5" cy="11.5" r="1.25" fill="currentColor" />
        <path
          d="M9.5 15C10.5 16.1667 13.5 16.1667 14.5 15"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    </div>
  )
}

function CopyButton({
  cta,
  label,
  copiedLabel,
}: {
  cta: ContactCTACopy
  label: string
  copiedLabel: string
}) {
  const [copied, setCopied] = useState(false)

  const handleClick = () => {
    navigator.clipboard.writeText(cta.value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors',
        'border',
        copied
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
          : 'bg-white text-muted border-cord/60 hover:border-maia hover:text-maia-dark'
      )}
    >
      {copied ? <Check className="w-3 h-3" /> : ctaIcons[cta.id]}
      <span>{copied ? copiedLabel : label}</span>
    </button>
  )
}

export function ChatMessage({ message, isTyping = false }: ChatMessageProps) {
  const { t } = useTranslation('common')
  const isUser = message.role === 'user'

  const formattedContent = useMemo(() => {
    if (!message.content) return null
    return parseMarkdownBold(message.content)
  }, [message.content])

  return (
    <div className={cn('flex gap-2', isUser ? 'flex-row-reverse' : 'flex-row')}>
      {!isUser && <BotAvatar />}

      <div className={cn('flex flex-col', isUser ? 'items-end' : 'items-start')}>
        <div
          className={cn(
            'max-w-[82%] rounded-2xl px-4 py-2.5',
            isUser
              ? 'bg-ink text-white rounded-br-sm'
              : 'bg-white text-ink rounded-bl-sm border border-cord/40 shadow-sm'
          )}
        >
          {isTyping ? (
            <div className="flex gap-1.5 py-1.5 px-1">
              <span
                className="w-2 h-2 bg-maia/60 rounded-full animate-bounce"
                style={{ animationDelay: '0ms' }}
              />
              <span
                className="w-2 h-2 bg-maia/60 rounded-full animate-bounce"
                style={{ animationDelay: '150ms' }}
              />
              <span
                className="w-2 h-2 bg-maia/60 rounded-full animate-bounce"
                style={{ animationDelay: '300ms' }}
              />
            </div>
          ) : (
            <p className="text-sm whitespace-pre-wrap leading-relaxed">
              {formattedContent}
            </p>
          )}
        </div>

        {/* Contact CTAs — only for assistant messages, not typing */}
        {!isUser && !isTyping && message.content && contactCTAs.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-2 ml-0.5">
            {contactCTAs.map((cta) =>
              cta.kind === 'copy' ? (
                <CopyButton
                  key={cta.id}
                  cta={cta}
                  label={t(cta.labelKey)}
                  copiedLabel={t('chatbot.cta.copied')}
                />
              ) : (
                <a
                  key={cta.id}
                  href={cta.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-white text-muted border border-cord/60 hover:border-maia hover:text-maia-dark transition-colors"
                >
                  {cta.icon && <img src={cta.icon} alt="" className="w-3 h-3" />}
                  <span>{t(cta.labelKey)}</span>
                </a>
              )
            )}
          </div>
        )}
      </div>
    </div>
  )
}
